/**
 * Sticky add-to-cart bar for the product page, phone widths only.
 *
 * On a phone the inline Add to cart sits roughly 1.6 screens down the page
 * (measured at 375x812: button top at 1292px against an 812px viewport), so a
 * buyer who has scrolled through the gallery and the description has no buy
 * action anywhere on screen. This bar restores one.
 *
 * It deliberately does NOT submit the cart itself. It forwards the click to
 * the real Add to cart button inside <product-form>, so Dawn's own form
 * handling, error rendering, loading spinner, cart drawer and analytics all
 * run exactly as they do for the inline button - there is one add-to-cart
 * code path, not two that can drift apart.
 */
class OvaranaStickyAtc extends HTMLElement {
  #observer = null;
  #sourceButton = null;
  #stateObserver = null;

  connectedCallback() {
    this.#sourceButton = document.getElementById(this.dataset.sourceButtonId);
    this.button = this.querySelector('[data-sticky-atc-button]');

    // No inline button to mirror means nothing to forward to. Stay hidden
    // rather than rendering a button that cannot work.
    if (!this.#sourceButton || !this.button) return;

    this.button.addEventListener('click', this.#handleClick);

    this.#syncState();
    this.#watchVisibility();
    this.#watchSourceState();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#stateObserver?.disconnect();
    this.button?.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (event) => {
    event.preventDefault();
    this.#sourceButton.click();
  };

  /**
   * Show the bar only once the inline button has left the viewport upwards.
   * Using the button itself as the sentinel means the bar never covers the
   * control it duplicates.
   */
  #watchVisibility() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.toggleAttribute('data-visible', scrolledPast);
        }
      },
      { threshold: 0 }
    );

    this.#observer.observe(this.#sourceButton);
  }

  /**
   * Mirror the inline button's disabled state, so a sold-out or unavailable
   * variant cannot be added from here when it cannot be added from there.
   */
  #watchSourceState() {
    this.#stateObserver = new MutationObserver(() => this.#syncState());
    this.#stateObserver.observe(this.#sourceButton, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: true,
      subtree: true,
    });
  }

  #syncState() {
    const { disabled } = this.#sourceButton;
    this.button.disabled = disabled;

    const label = this.#sourceButton.querySelector('span')?.textContent.trim();
    const target = this.button.querySelector('[data-sticky-atc-label]');
    if (label && target) target.textContent = label;
  }
}

customElements.define('ovarana-sticky-atc', OvaranaStickyAtc);
